/**
 * Resolves whether a previously selected database exists on a new connection.
 * Used when switching connections to preserve database context if possible.
 */

import { useConnectionStore } from '../../../connections/store/connectionStore';
import { findDatabaseInList } from '../../../connections/utils/connectionDatabaseLimit';
import { readDatabaseListingThroughCache } from '../../../database-objects/hooks/useDatabaseQueries';

/**
 * Checks if `previousDatabase` exists on the given connection.
 * Returns the LISTED database name if found, undefined otherwise.
 *
 * [CP2-DEFER-3] the match is case-INSENSITIVE (an exact match still wins),
 * matching the central `getDatabases` limit filter — SQL Server identifiers are
 * case-insensitive by default, so a connection limited to `dbcam` whose server
 * lists `DBCAM` must keep its database context across a connection switch
 * instead of silently dropping it.
 */
export async function resolveDatabase(
  connectionId: string,
  previousDatabase: string | undefined
): Promise<string | undefined> {
  if (!previousDatabase) return undefined;
  try {
    const { connections, activeConnections } = useConnectionStore.getState();
    const engine =
      connections.find((c) => c.id === connectionId)?.engine ||
      activeConnections.find((c) => c.id === connectionId)?.connection.engine ||
      'mssql';
    // Through the shared listing slot: this runs on a user action (switching
    // connection), so joining a listing the Explorer already has in flight —
    // and leaving the answer where the next picker will read it — is free.
    const result = await readDatabaseListingThroughCache(connectionId, engine);
    // Only trust the list when the listing actually succeeded — on `'error'`
    // the array is the engine fallback (Slice 5b) and would falsely confirm
    // common system DBs like `master`.
    if (result.state !== 'ok') return undefined;
    return findDatabaseInList(result.databases, previousDatabase);
  } catch {
    // Silently fail — database stays cleared
  }
  return undefined;
}
